import React, { useMemo, useState, useEffect } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import DashboardLayout from "../../layouts/DashboardLayout";
import StoreBreadcrumbs from "./components/StoreBreadcrumbs";
import ProductTypeModal, {
  queryToProductType,
} from "./components/ProductTypeModal/ProductTypeModal";
import { useProductForm } from "./components/ProductForm/useProductForm";
import ProductDetailsSection from "./components/ProductForm/sections/ProductDetailsSection";
import CharacteristicsSection from "./components/ProductForm/sections/CharacteristicsSection";
import PricingSection from "./components/ProductForm/sections/PricingSection";
import VariationsSection from "./components/ProductForm/sections/VariationsSection";
import ImagesSection from "./components/ProductForm/sections/ImagesSection";
import DigitalFilesSection from "./components/ProductForm/sections/DigitalFilesSection";
import ProductFormFooter from "./components/ProductForm/sections/ProductFormFooter";
import CostCalculator from "../../components/NewProduct/components/CostCalculator";
import ProductService from "../../../../service/ProductService";
import {
  CUSTOMER_MAX_PERCENT_CASHBACK,
  CUSTOMER_MIN_PERCENT_CASHBACK,
  MANAGER_MAX_PERCENT_CASHBACK,
  MANAGER_MIN_PERCENT_CASHBACK,
  PARTNER_MAX_PERCENT_CASHBACK,
  PARTNER_MIN_PERCENT_CASHBACK,
  SELLER_MAX_PERCENT_CASHBACK,
  SELLER_MIN_PERCENT_CASHBACK,
} from "../../../../utilities/constants";
import "./components/ProductForm/ProductForm.css";

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

const cashbackByPlan = (plan) => {
  switch (plan) {
    case "Customer":
      return { min: CUSTOMER_MIN_PERCENT_CASHBACK, max: CUSTOMER_MAX_PERCENT_CASHBACK };
    case "Partner":
      return { min: PARTNER_MIN_PERCENT_CASHBACK, max: PARTNER_MAX_PERCENT_CASHBACK };
    case "Manager":
      return { min: MANAGER_MIN_PERCENT_CASHBACK, max: MANAGER_MAX_PERCENT_CASHBACK };
    case "Seller":
      return { min: SELLER_MIN_PERCENT_CASHBACK, max: SELLER_MAX_PERCENT_CASHBACK };
    default:
      return { min: CUSTOMER_MIN_PERCENT_CASHBACK, max: CUSTOMER_MAX_PERCENT_CASHBACK };
  }
};

function ProductFormContent({ productType, productId, userInfo, plan, currencyCode }) {
  const form = useProductForm({ productType, productId, userInfo, plan, currencyCode });
  const bounds = cashbackByPlan(plan);

  if (form.loading) {
    return (
      <div className="flex justify-content-center align-items-center py-8">
        <i className="pi pi-spin pi-spinner text-4xl text-500" />
      </div>
    );
  }

  return (
    <div className="product-form-page">
      <ProductDetailsSection form={form} />
      <CharacteristicsSection form={form} />
      <PricingSection form={form} />
      <VariationsSection form={form} />
      <ImagesSection form={form} />
      <DigitalFilesSection form={form} />
      <ProductFormFooter form={form} />
      {form.showCostCalculator && (
        <CostCalculator
          onHide={form.closeCostCalculator}
          isVisible={form.showCostCalculator}
          userCurrency={currencyCode}
          selectedCategory={form.selectedCategory}
          selectedSubcategory={form.selectedSubcategory}
          selectedTypeProduct={productType}
          notHaveFreeSendCloud={form.notHaveFreeSendCloud}
          percentMax={bounds.max}
          percentMin={bounds.min}
          userInfo={userInfo}
          currencyCode={currencyCode}
          setProductPrice={form.setPriceUnique}
          setProductCashbackPercentage={form.setPercentExtraCoin}
          setProductWeight={form.setWeight}
          setProductHeight={form.setHeight}
          setProductWidth={form.setWidth}
          setProductLength={form.setLength}
          initialPrice={form.priceUnique}
          initialCashbackPercent={form.percentExtraCoin}
          initialHeight={form.height}
          initialWidth={form.width}
          initialLength={form.length}
          initialWeightKg={form.weight}
          setProductFreightOption={form.setFreightOption}
        />
      )}
    </div>
  );
}

const StoreProductFormPage = ({ userInfo, plan, currencyCode = "EUR" }) => {
  const history = useHistory();
  const query = useQuery();
  const { productId } = useParams();
  const typeFromQuery = queryToProductType(query.get("type"));
  const isEdit = !!productId;
  const [inferredType, setInferredType] = useState(null);

  useEffect(() => {
    if (!isEdit) return undefined;
    let cancelled = false;
    (async () => {
      const service = new ProductService();
      const res = await service.getProductById(productId);
      if (cancelled) return;
      const product = res?.result?.product;
      if (!product) return;
      if (product.service) setInferredType(2);
      else if (product.downloadable) setInferredType(3);
      else setInferredType(1);
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, productId]);

  const productType = isEdit ? inferredType : typeFromQuery;
  const showTypeModal = !isEdit && !typeFromQuery;

  return (
    <DashboardLayout>
      <StoreBreadcrumbs />
      {showTypeModal && (
        <ProductTypeModal visible onHide={() => history.push("/dashboard-store/products")} />
      )}
      {productType ? (
        <ProductFormContent
          productType={productType}
          productId={productId}
          userInfo={userInfo}
          plan={plan}
          currencyCode={currencyCode}
        />
      ) : isEdit ? (
        <div className="flex justify-content-center align-items-center py-8">
          <i className="pi pi-spin pi-spinner text-4xl text-500" />
        </div>
      ) : null}
    </DashboardLayout>
  );
};

export default StoreProductFormPage;
